import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Grid,
} from '@mui/material';
import {
  EditNote as EditRequestIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CompareArrows as DiffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as articleService from '../services/articleService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import AssigneeCell from '../components/common/AssigneeCell';

// ==================== FIELD LABELS ====================

const fieldLabels: Record<string, string> = {
  title: 'عنوان',
  summary: 'خلاصه',
  excerpt: 'خلاصه',
  content: 'محتوا',
  contentHtml: 'محتوای HTML',
  featuredImage: 'تصویر شاخص',
  coverImage: 'تصویر کاور',
  images: 'تصاویر',
  categoryId: 'دسته‌بندی',
  category: 'دسته‌بندی',
  tags: 'تگ‌ها',
  seriesId: 'مجموعه',
  seriesOrder: 'ترتیب در مجموعه',
  metaTitle: 'متا تایتل',
  metaDescription: 'متا توضیحات',
  metaKeywords: 'کلمات کلیدی',
  canonicalUrl: 'لینک کانونیکال',
  readTime: 'زمان مطالعه',
  slug: 'اسلاگ',
};

const getFieldLabel = (field: string): string => {
  return fieldLabels[field] || field;
};

// ==================== DIFF VIEWER ====================

interface DiffViewerProps {
  originalData: Record<string, any>;
  editData: Record<string, any>;
  changedFields: string[];
}

const imageFields = new Set(['featuredImage', 'coverImage']);
const imageArrayFields = new Set(['images']);

const isImageUrl = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(value) || value.startsWith('data:image/') || value.includes('/uploads/');
};

const DiffViewer: React.FC<DiffViewerProps> = ({ originalData, editData, changedFields }) => {
  if (!changedFields || changedFields.length === 0) {
    return (
      <Alert severity="info">تغییری یافت نشد</Alert>
    );
  }

  const renderValue = (value: any, field?: string): string => {
    if (value === null || value === undefined) return '(خالی)';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.map((v) => (typeof v === 'object' ? v.name || JSON.stringify(v) : v)).join('، ');
      }
      return value.name || JSON.stringify(value);
    }
    if (typeof value === 'string' && value.length > 300) {
      return value.substring(0, 300) + '...';
    }
    return String(value);
  };

  const renderImage = (src: string, alt: string) => (
    <Box>
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, objectFit: 'contain' }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    </Box>
  );

  const renderImageArray = (images: string[], alt: string) => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${alt} ${i + 1}`}
          style={{ maxWidth: 120, maxHeight: 90, borderRadius: 4, objectFit: 'cover', border: '1px solid #eee' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ))}
    </Box>
  );

  const renderFieldValue = (value: any, field: string, label: string) => {
    // Single image field
    if (imageFields.has(field) && value && typeof value === 'string' && isImageUrl(value)) {
      return renderImage(value, label);
    }
    // Array of images
    if (imageArrayFields.has(field) && Array.isArray(value) && value.length > 0 && value.every((v: any) => typeof v === 'string')) {
      return renderImageArray(value, label);
    }
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {renderValue(value, field)}
      </Typography>
    );
  };

  return (
    <Box>
      {changedFields.map((field) => (
        <Box key={field} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {getFieldLabel(field)}
          </Typography>
          <Grid container spacing={2}>
            {/* Original value */}
            <Grid item xs={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'error.50',
                  borderColor: 'error.200',
                  minHeight: 60,
                }}
              >
                <Typography variant="caption" color="error.main" fontWeight={600} display="block" gutterBottom>
                  مقدار فعلی
                </Typography>
                {renderFieldValue(originalData[field], field, 'تصویر فعلی')}
              </Paper>
            </Grid>

            {/* New value */}
            <Grid item xs={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'success.50',
                  borderColor: 'success.200',
                  minHeight: 60,
                }}
              >
                <Typography variant="caption" color="success.main" fontWeight={600} display="block" gutterBottom>
                  مقدار جدید
                </Typography>
                {renderFieldValue(editData[field], field, 'تصویر جدید')}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

// ==================== MAIN COMPONENT ====================

const ArticleEditRequests: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [editRequests, setEditRequests] = useState<articleService.EditRequest[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');

  // Dialog state
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<articleService.EditRequest | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load edit requests
  const loadEditRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await articleService.getEditRequests(page + 1, rowsPerPage, {
        status: statusFilter || undefined,
      });
      setEditRequests(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری درخواست‌های ویرایش');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    loadEditRequests();
  }, [loadEditRequests]);

  // ==================== HANDLERS ====================

  const handleViewDiff = (request: articleService.EditRequest) => {
    setSelectedRequest(request);
    setDiffDialogOpen(true);
  };

  const handleApproveClick = (request: articleService.EditRequest) => {
    setSelectedRequest(request);
    setApprovalNote('');
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedRequest) return;
    setSubmitting(true);
    try {
      await articleService.approveEditRequest(selectedRequest._id, approvalNote || undefined);
      enqueueSnackbar('درخواست ویرایش تایید شد', { variant: 'success' });
      setApproveDialogOpen(false);
      loadEditRequests();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تایید درخواست', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectClick = (request: articleService.EditRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      enqueueSnackbar('لطفا دلیل رد را وارد کنید', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await articleService.rejectEditRequest(selectedRequest._id, rejectionReason);
      enqueueSnackbar('درخواست ویرایش رد شد', { variant: 'success' });
      setRejectDialogOpen(false);
      loadEditRequests();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در رد درخواست', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  // ==================== RENDER ====================

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <MyShiftStatusBanner category="article_edit" />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <EditRequestIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          درخواست‌های ویرایش مقالات
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadEditRequests} disabled={loading}>
          بروزرسانی
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>وضعیت</InputLabel>
          <Select
            value={statusFilter}
            label="وضعیت"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="pending">در انتظار بررسی</MenuItem>
            <MenuItem value="approved">تایید شده</MenuItem>
            <MenuItem value="rejected">رد شده</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>عنوان مقاله</TableCell>
                <TableCell>درخواست‌دهنده</TableCell>
                <TableCell>فیلدهای تغییر یافته</TableCell>
                <TableCell>تاریخ</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>مسئول</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : editRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">درخواست ویرایشی یافت نشد</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                editRequests.map((request) => (
                  <TableRow key={request._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {request.articleId?.title || request.article?.title || 'نامشخص'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.createdBy?.name || request.requestedBy?.name || 'نامشخص'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.createdBy?.email || request.requestedBy?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {request.changes && request.changes.length > 0 ? (
                          request.changes.map((c) => (
                            <Chip
                              key={c.fieldName}
                              label={c.fieldNameFa || getFieldLabel(c.fieldName)}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))
                        ) : (request.changedFields || []).map((field) => (
                          <Chip
                            key={field}
                            label={getFieldLabel(field)}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(request.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={articleService.getEditRequestStatusLabel(request.status)}
                        color={articleService.getEditRequestStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <AssigneeCell value={(request as any).assignedTo} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="مشاهده تغییرات">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewDiff(request)}
                          >
                            <DiffIcon />
                          </IconButton>
                        </Tooltip>
                        {request.status?.toUpperCase() === 'PENDING' && (
                          <>
                            <Tooltip title="تایید">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => handleApproveClick(request)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="رد">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRejectClick(request)}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} از ${count}`}
        />
      </Paper>

      {/* ==================== DIFF DIALOG ==================== */}
      <Dialog
        open={diffDialogOpen}
        onClose={() => setDiffDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiffIcon />
            مقایسه تغییرات
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  مقاله: {selectedRequest.articleId?.title || selectedRequest.article?.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  درخواست‌دهنده: {selectedRequest.createdBy?.name || selectedRequest.requestedBy?.name} | تاریخ: {formatDate(selectedRequest.createdAt)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <DiffViewer
                originalData={selectedRequest.originalData || {}}
                editData={selectedRequest.editData || {}}
                changedFields={selectedRequest.changes?.map(c => c.fieldName) || selectedRequest.changedFields || []}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest?.status?.toUpperCase() === 'PENDING' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setDiffDialogOpen(false);
                  if (selectedRequest) handleApproveClick(selectedRequest);
                }}
                startIcon={<ApproveIcon />}
              >
                تایید
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setDiffDialogOpen(false);
                  if (selectedRequest) handleRejectClick(selectedRequest);
                }}
                startIcon={<RejectIcon />}
              >
                رد
              </Button>
            </>
          )}
          <Button onClick={() => setDiffDialogOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== APPROVE DIALOG ==================== */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تایید درخواست ویرایش</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="یادداشت تایید (اختیاری)"
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : <ApproveIcon />}
          >
            تایید
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== REJECT DIALOG ==================== */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>رد درخواست ویرایش</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="دلیل رد"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            required
            sx={{ mt: 1 }}
            error={!rejectionReason.trim() && rejectDialogOpen}
            helperText={!rejectionReason.trim() ? 'وارد کردن دلیل رد الزامی است' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={submitting || !rejectionReason.trim()}
            startIcon={submitting ? <CircularProgress size={18} /> : <RejectIcon />}
          >
            رد درخواست
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ArticleEditRequests;
